import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import { useDriverStateV2 } from '../../hooks/useDriverStateV2';
import type { DriverSortBy, ListParams } from '@relay-towing/api';
import { getList } from '../list';

export interface CompanyDriver {
  id: string;
  name: string;
  truckNumber: string | null;
  locationLatitude: number;
  locationLongitude: number;
  status: 'ACTIVE' | 'CLEAR' | 'DISPATCHED' | 'ON_LEAVE';
  email: string;
  phone: string | null;
  user?: { id: string };
}

export const useGetAllDrivers = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();

  return useQuery({
    queryKey: ['all-drivers', driver?.companyId],
    queryFn: async () => {
      if (!driver?.companyId) {
        throw new Error('Company ID not available');
      }

      const token = await getAccessTokenSilently();

      // The old request sent `page=1`, which /drivers never read; pageNo is
      // the name it pages by, and 1 is its default either way.
      // Whoever drives, in the server's order.
      const params = {
        pageNo: 1,
        pageSize: 1000,
      } satisfies ListParams<DriverSortBy>;
      const { data: drivers } = await getList<CompanyDriver>(
        '/drivers',
        params,
        { token, companyId: driver.companyId }
      );
      return drivers;
    },
    enabled: !!driver?.companyId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every 60 seconds for location updates
  });
};
